import { Logo } from '@/components';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Avatar,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ChartBarIcon,
  PhoneIcon,
  PencilIcon,
  UserPlusIcon,
  ClockIcon,
  FlaskIcon,
  ChartLineUpIcon,
  PlugIcon,
  ShieldCheckIcon,
  FileTextIcon,
  UserCheckIcon,
  ChartPieIcon
} from '@phosphor-icons/react';
import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { SnackbarProvider } from '../context/SnackbarContext';

// Define the sidebar width
const drawerWidth = 280;

// Interface for navigation item
interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
}

// Interface for navigation section
interface NavSection {
  title: string;
  items: NavItem[];
}

// Create a styled component for the main content
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  height: '100vh',
  overflow: 'auto',
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

/**
 * Layout for Voice Demo pages
 */
const VoiceDemoLayout: React.FC = () => {
  const location = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  // Navigation sections for voice demo
  const navSections: NavSection[] = [
    {
      title: 'Dashboard',
      items: [
        { title: 'Overview', path: '/voice_demo', icon: <ChartBarIcon size={24} /> },
      ]
    },
    {
      title: 'Campaign Management',
      items: [
        { title: 'Call History', path: '/voice_demo/calls', icon: <PhoneIcon size={24} /> },
        { title: 'Script Editor', path: '/voice_demo/scripts', icon: <PencilIcon size={24} /> },
        { title: 'Contact Management', path: '/voice_demo/contacts', icon: <UserPlusIcon size={24} /> },
        { title: 'Follow-ups', path: '/voice_demo/followups', icon: <ClockIcon size={24} /> },
      ]
    },
    {
      title: 'Fraud Prevention',
      items: [
        { title: 'Verification Dashboard', path: '/voice_demo/verification', icon: <ShieldCheckIcon size={24} /> },
        { title: 'Document Verification', path: '/voice_demo/verification/documents', icon: <FileTextIcon size={24} /> },
        { title: 'Identity Verification', path: '/voice_demo/verification/identity', icon: <UserCheckIcon size={24} /> },
        { title: 'Analytics & ROI', path: '/voice_demo/verification/analytics', icon: <ChartPieIcon size={24} /> },
      ]
    },
    {
      title: 'Analytics',
      items: [
        { title: 'A/B Testing', path: '/voice_demo/ab_testing', icon: <FlaskIcon size={24} /> },
        { title: 'Mass Insights', path: '/voice_demo/insights', icon: <ChartLineUpIcon size={24} /> },
      ]
    },
    {
      title: 'Integration',
      items: [
        { title: 'Data Export', path: '/voice_demo/integration', icon: <PlugIcon size={24} /> },
      ]
    }
  ];

  // Mock user for demo
  const mockUser = {
    name: 'Sarah Rodriguez',
    email: 'sarah@educationplus.com',
    role: 'Campaign Manager',
    organization: 'Education Plus International'
  };

  if (isMobile) {
    return (
      <Stack direction='column' alignItems='center' justifyContent='center' height='100vh' p={2} textAlign='center'>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          📱 AI Voice Calling Platform Demo
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          For the best demo experience, please use a desktop or laptop browser.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This demo showcases our AI calling platform for education providers.
        </Typography>
      </Stack>
    );
  }

  return (
    <SnackbarProvider>
      <Stack direction="row">
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            height: '100%',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: 'none',
              height: '100%',
            },
          }}
          variant="persistent"
          anchor="left"
          open={true}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              padding: theme => theme.spacing(2),
              height: 80,
            }}>
            <Logo />
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              DEMO
            </Box>
          </Stack>

          {/* Demo User Profile */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} 
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                p: 2, 
                borderRadius: 2 
              }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {mockUser.name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" noWrap>
                  {mockUser.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {mockUser.role}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {mockUser.organization}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack sx={{ overflow: 'hidden', height: '100%', px: 2 }}>
            <Box sx={{ overflow: 'auto' }}>
              {navSections.map((section, outerIndex) => (
                <React.Fragment key={section.title}>
                  {outerIndex > 0 && <Divider sx={{ my: 1 }} />}
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 1,
                      mt: outerIndex === 0 ? 0 : 2,
                      display: 'block'
                    }}
                  >
                    {section.title}
                  </Typography>
                  <List sx={{ pl: 0, pb: 1 }}>
                    {section.items.map((item) => {
                      const isSelected = location.pathname === item.path || 
                        (location.pathname === '/voice_demo/' && item.path === '/voice_demo');
                      return (
                        <ListItem key={item.path} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.path}
                            sx={{
                              borderRadius: '8px',
                              py: 0.75,
                              px: 1.5,
                              my: 0.25,
                              backgroundColor: isSelected ? 'primary.main' : 'transparent',
                              color: isSelected ? 'white' : 'text.primary',
                              '&:hover': {
                                backgroundColor: isSelected ? 'primary.main' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 32,
                              color: isSelected ? 'white' : 'text.secondary'
                            }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              slotProps={{
                                primary: {
                                  sx: {
                                    fontSize: '0.875rem',
                                    fontWeight: isSelected ? 500 : 400
                                  }
                                }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </React.Fragment>
              ))}
            </Box>
            
            {/* Demo Notice */}
            <Box sx={{ mt: 'auto', mb: 2 }}>
              <Box
                sx={{
                  backgroundColor: 'warning.light',
                  color: 'warning.contrastText',
                  p: 2,
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  🚀 Demo Platform
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Showcasing AI voice calling capabilities for education providers
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Drawer>
        <Main open={true} sx={{ padding: { xs: 2, md: 3, lg: 4, xl: 6 } }}>
          <Outlet />
        </Main>
      </Stack>
    </SnackbarProvider>
  );
};

export default VoiceDemoLayout;